import { useMemo, useState } from "react";

import type { MemberRevenueStatisticVo } from "@zhihaoscm/entity";
import { dashboardService } from "@/api/services/dashboardService";
import { getLastYearMonths } from "@/utils";
import DashboardChart from "../chart";
import { getTtackBarOption } from "../chartDefaultOption";
import {
    DashboardDateSelect,
    type DashboardDateValue,
    DataWrap,
    toDashboardAnalysisParams,
    useDashboardBarStyle,
    useDashboardQuery,
    WidgetBody
} from "../dashboardWidgets";

/** 会员费用新增分析，复刻 Vue 的三系列堆叠柱状图。 */
export default function AddMemberRevenueAnalysis() {
    const [time, setTime] = useState<DashboardDateValue>(3);
    const { barWidth, barBorderRadius } = useDashboardBarStyle();
    const { data, loading, error } = useDashboardQuery<Partial<MemberRevenueStatisticVo>>(
        () => dashboardService.getAddMemberRevenueAnalysis(toDashboardAnalysisParams(time)),
        [time]
    );
    const xAxisData = useMemo(() => {
        if (typeof time === "string") return Array.from({ length: 12 }, (_, index) => `${index + 1}月`);
        return getLastYearMonths(time === 4 ? 12 : 3);
    }, [time]);
    const option = useMemo(
        () =>
            getTtackBarOption({
                titleText: "费用（万元）",
                xAxisData,
                barBorderRadius,
                series: [
                    {
                        data: data?.totalBaseMemberCosts || [],
                        type: "bar",
                        stack: "企业会员",
                        name: "基础会员",
                        barWidth,
                        itemStyle: { color: "#1777ff" }
                    },
                    {
                        data: data?.totalSeniorMemberCosts || [],
                        type: "bar",
                        stack: "企业会员",
                        name: "高级会员",
                        barWidth,
                        itemStyle: { color: "#ffae4c" }
                    },
                    {
                        data: data?.totalEnterpriseGradeMemberCosts || [],
                        type: "bar",
                        stack: "企业会员",
                        name: "企业会员",
                        barWidth,
                        itemStyle: { color: "#6fd195" }
                    }
                ]
            }),
        [barBorderRadius, barWidth, data, xAxisData]
    );

    return (
        <DataWrap
            title="会员费用新增分析"
            className="add-member-revenue-analysis"
            option={
                <DashboardDateSelect
                    value={time}
                    onChange={setTime}
                    options={["quarter", "year"]}
                    defaultActive="quarter"
                    pickerType="year"
                />
            }
        >
            <WidgetBody loading={loading} error={error}>
                <div className="dashboard-analysis-main">
                    <DashboardChart option={option} ariaLabel="会员费用新增分析柱状图" />
                </div>
            </WidgetBody>
        </DataWrap>
    );
}
